
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useHasPermission, usePermissions } from '@contexts/permissions-provider';
import { APP_ROUTES } from '@routes/routes.constants';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { AccessPassWidgetProps } from './AccessPassWidget.types';

/**
 * AccessPassWidget - MFE wrapper for AccessPassWidget
 *
 * This feature loads the remote AccessPassWidget MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in CommonHomeContent):
 * 1. User must be logged in
 * 2. Features - accessPageEnabledWebApp must be enabled
 * 3. Permissions - AccessPassNoTraining_View_Own required
 *
 */
const AccessPassWidget = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const hasNoTrainingAccess = useHasPermission('AccessPassNoTraining_View_Own');
  const { accessPageEnabledWebApp } = useFlags();

  const canViewAccessPass = hasNoTrainingAccess && accessPageEnabledWebApp;

  const [RemoteComponent, setRemoteComponent] =
    React.useState<React.ComponentType<AccessPassWidgetProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  const navigateToAccessPassPage = React.useCallback((): void => {
    navigate(APP_ROUTES.ACCESS_PASS);
  }, [navigate]);

  React.useEffect(() => {
    const loadComponent = async (): Promise<void> => {
      try {
        const { default: Component } =
          await import('access_pass_mfe/AccessPassWidget');
        setRemoteComponent(() => Component);
      } catch (error) {
        console.error('Error loading AccessPassWidget:', error);
        setLoadError(error as Error);
      }
    };

    loadComponent();
  }, []);

  if (!canViewAccessPass || loadError) {
    return null;
  }

  if (!RemoteComponent) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteComponent
        msalInstance={instance}
        user={authData}
        permissions={permissions}
        navigateToAccessPassPage={navigateToAccessPassPage}
      />
    </React.Suspense>
  );
});

AccessPassWidget.displayName = 'AccessPassWidget';

export { AccessPassWidget };

